import { AISparkles } from "@/components/ui/ai-sparkles";
import { CountBadge } from "@/components/shared/CountBadge";
import { cn } from "@/lib/utils";

export type StrategyFilterValue = "recommended" | "completed" | "dismissed";

interface StrategyFilterChipsProps {
  value: StrategyFilterValue;
  onChange: (value: StrategyFilterValue) => void;
  recommendedCount: number;
  completedCount: number;
  dismissedCount: number;
  className?: string;
}

interface ChipDef {
  value: StrategyFilterValue;
  label: string;
  count: number;
  /** The AI-generated filter (`Recommended`) renders the sparkle + AI gradient border. */
  ai: boolean;
}

/**
 * `chip-ai-gradient-border-selected` / `alert-ai-gradient-border-plain` are the design's AI
 * gradient border (`src/index.css`). The inactive plain chip carries the same
 * `border-input bg-card hover:bg-primary/15` look as the `secondary` button variant (AUT-623
 * realigned that variant to the design), but stays a plain `<button>` because a chip needs its own
 * pill shape and the AI-gradient active states, not a CTA button.
 */
function chipClasses(active: boolean, ai: boolean): string {
  return cn(
    "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-sm font-medium text-foreground transition-colors",
    !ai && active && "bg-primary-tint-medium border-primary hover:bg-primary-tint-strong",
    !ai && !active && "border-input bg-card hover:bg-primary/15",
    ai && "border-transparent",
    ai && active && "chip-ai-gradient-border-selected",
    ai && !active && "alert-ai-gradient-border-plain",
  );
}

/**
 * The three filter chips under "Other strategies" — Recommended (the AI chip), Completed,
 * Dismissed. Built on a plain `<button>` (not `Button variant="secondary"`) because a chip needs
 * its own pill shape and the AI-gradient active states, see `chipClasses` above.
 */
export function StrategyFilterChips({
  value,
  onChange,
  recommendedCount,
  completedCount,
  dismissedCount,
  className,
}: StrategyFilterChipsProps) {
  const chips: ChipDef[] = [
    { value: "recommended", label: "Recommended", count: recommendedCount, ai: true },
    { value: "completed", label: "Completed", count: completedCount, ai: false },
    { value: "dismissed", label: "Dismissed", count: dismissedCount, ai: false },
  ];

  return (
    <div className={cn("flex items-center gap-2 flex-wrap", className)}>
      {chips.map((chip) => {
        const active = chip.value === value;
        return (
          <button
            key={chip.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(chip.value)}
            className={chipClasses(active, chip.ai)}
          >
            {chip.ai && <AISparkles className="h-3.5 w-3.5" />}
            {chip.label}
            {chip.count > 0 && <CountBadge size="sm" count={chip.count} />}
          </button>
        );
      })}
    </div>
  );
}
